// src/componants/common/AssistantChat.js
import { useEffect, useRef, useState } from "react";
import { Sparkles, X, Send, Loader2 } from "lucide-react";
import { sendAssistantMessage } from "../../api/assistant";
import "./AssistantChat.css";

const MAX_HISTORY_MESSAGES = 10;
const GREETING = {
  role: "model",
  text: "أهلاً! أنا مساعدك في الموارد البشرية. اسألني عن رصيد إجازتك أو حضورك النهارده أو طلباتك المعلّقة.",
};
const QUICK_QUESTIONS = [
  "رصيد إجازتي كام؟",
  "حضور النهارده؟",
  "في طلبات معلّقة؟",
];

function AssistantChat() {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([GREETING]);
  const [input, setInput] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState("");
  const messagesEndRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return;
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, isOpen, isSending]);

  async function handleSend(text) {
    const trimmed = (text ?? input).trim();
    if (!trimmed || isSending) return;

    const userMessage = { role: "user", text: trimmed };
    const nextMessages = [...messages, userMessage];
    setMessages(nextMessages);
    setInput("");
    setError("");
    setIsSending(true);

    try {
      const history = nextMessages
        .filter((m) => m !== GREETING)
        .slice(-MAX_HISTORY_MESSAGES - 1, -1)
        .map((m) => ({ role: m.role, text: m.text }));

      const result = await sendAssistantMessage(trimmed, history);
      setMessages((prev) => [
        ...prev,
        { role: "model", text: result?.reply || "معذرة، لم أستطع الرد الآن." },
      ]);
    } catch (err) {
      const apiMessage = err?.response?.data?.message;
      setError(apiMessage || "تعذّر الاتصال بالمساعد، حاول مرة أخرى.");
    } finally {
      setIsSending(false);
    }
  }

  function handleKeyDown(e) {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  }

  return (
    <div className="assistant-chat-root">
      {isOpen && (
        <div className="assistant-chat-panel" role="dialog" aria-label="المساعد الذكي">
          <div className="assistant-chat-header">
            <div className="assistant-chat-title">
              <Sparkles size={18} />
              <span>المساعد الذكي</span>
            </div>
            <button
              type="button"
              className="assistant-chat-close"
              onClick={() => setIsOpen(false)}
              aria-label="إغلاق"
            >
              <X size={18} />
            </button>
          </div>

          <div className="assistant-chat-messages">
            {messages.map((m, idx) => (
              <div
                key={idx}
                className={`assistant-chat-bubble assistant-chat-bubble-${m.role}`}
              >
                {m.text}
              </div>
            ))}
            {isSending && (
              <div className="assistant-chat-bubble assistant-chat-bubble-model assistant-chat-typing">
                <Loader2 size={16} className="assistant-chat-spin" />
                <span>بيكتب...</span>
              </div>
            )}
            {error && <div className="assistant-chat-error">{error}</div>}
            <div ref={messagesEndRef} />
          </div>

          {messages.length <= 1 && !isSending && (
            <div className="assistant-chat-quick">
              {QUICK_QUESTIONS.map((q) => (
                <button
                  key={q}
                  type="button"
                  className="assistant-chat-chip"
                  onClick={() => handleSend(q)}
                >
                  {q}
                </button>
              ))}
            </div>
          )}

          <div className="assistant-chat-input-row">
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="اكتب سؤالك هنا..."
              disabled={isSending}
              className="assistant-chat-input"
            />
            <button
              type="button"
              className="assistant-chat-send"
              onClick={() => handleSend()}
              disabled={isSending || !input.trim()}
              aria-label="إرسال"
            >
              <Send size={18} />
            </button>
          </div>
        </div>
      )}

      <button
        type="button"
        className="assistant-chat-fab"
        onClick={() => setIsOpen((prev) => !prev)}
        aria-label="المساعد الذكي"
      >
        {isOpen ? <X size={22} /> : <Sparkles size={22} />}
      </button>
    </div>
  );
}

export default AssistantChat;
